import { SupportedLanguage } from '../i18n/translations';

export type TopicSlug =
  | 'customs-brokers-syria'
  | 'customs-clearance-latakia'
  | 'customs-duty-estimate'
  | 'container-customs-clearance'
  | 'nasib-border-customs'
  | 'reefer-customs-clearance'
  | 'shipping-usa-syria'
  | 'shipping-china-syria'
  | 'china-fcl-lcl'
  | 'fcl-shipping'
  | 'lcl-shipping'
  | 'air-freight';

interface TopicCopy {
  name: string;
  title: string;
  description: string;
  h1: string;
  intro: string;
  paragraphs: string[];
  benefitsTitle: string;
  benefits: string[];
  faqTitle: string;
  faqs: Array<{ q: string; a: string }>;
  cta: string;
  quoteCta: string;
}

export const topicSlugs: TopicSlug[] = [
  'customs-brokers-syria',
  'customs-clearance-latakia',
  'customs-duty-estimate',
  'container-customs-clearance',
  'nasib-border-customs',
  'reefer-customs-clearance',
  'shipping-usa-syria',
  'shipping-china-syria',
  'china-fcl-lcl',
  'fcl-shipping',
  'lcl-shipping',
  'air-freight',
];

/** Hero / card images for SEO topic pages */
export const topicImages: Record<
  TopicSlug,
  { src: string; alt: Record<SupportedLanguage, string> }
> = {
  'customs-brokers-syria': {
    src: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1200&h=675&fit=crop',
    alt: {
      ar: 'شركة الشراع للتخليص الجمركي في سوريا من اللاذقية',
      en: 'Al-Shiraa customs clearance company in Syria, based in Latakia',
      de: 'Al-Shiraa Zollabfertigung in Syrien, Sitz Latakia',
    },
  },
  'customs-clearance-latakia': {
    src: 'https://images.unsplash.com/photo-1605745341112-85968b19335b?w=1200&h=675&fit=crop',
    alt: {
      ar: 'تخليص جمركي وشحن بحري عبر الموانئ',
      en: 'Customs clearance and ocean freight at port',
      de: 'Zollabfertigung und Seefracht im Hafen',
    },
  },
  'customs-duty-estimate': {
    src: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=675&fit=crop',
    alt: {
      ar: 'حساب وتقدير الرسوم الجمركية',
      en: 'Customs duty estimate and paperwork',
      de: 'Zollschätzung und Unterlagen',
    },
  },
  'container-customs-clearance': {
    src: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&h=675&fit=crop',
    alt: {
      ar: 'تخليص حاويات في ساحة الميناء',
      en: 'Container customs clearance at terminal',
      de: 'Container-Zollabfertigung am Terminal',
    },
  },
  'nasib-border-customs': {
    src: 'https://images.unsplash.com/photo-1587385788270-742769b876c3?w=1200&h=675&fit=crop',
    alt: {
      ar: 'شحن بري وتخليص عبر المعابر',
      en: 'Land freight and border customs clearance',
      de: 'Landverkehr und Grenzzoll',
    },
  },
  'reefer-customs-clearance': {
    src: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1200&h=675&fit=crop',
    alt: {
      ar: 'حاوية مبرّدة للشحن الغذائي',
      en: 'Refrigerated reefer container for food cargo',
      de: 'Kühlcontainer für Lebensmittel',
    },
  },
  'shipping-usa-syria': {
    src: '/topics/shipping-usa-syria.jpg',
    alt: {
      ar: 'شحن بحري بالحاويات من أمريكا إلى سوريا',
      en: 'Ocean container shipping USA to Syria',
      de: 'Seefracht Container USA nach Syrien',
    },
  },
  'china-fcl-lcl': {
    src: '/topics/shipping-china-syria.jpg',
    alt: {
      ar: 'شحن كلي وجزئي من الصين إلى سوريا',
      en: 'FCL and LCL shipping from China to Syria',
      de: 'FCL- und LCL-Versand von China nach Syrien',
    },
  },
  'shipping-china-syria': {
    src: '/topics/shipping-china-syria.jpg',
    alt: {
      ar: 'شحن حاويات من الصين إلى سوريا',
      en: 'Container shipping from China to Syria',
      de: 'Containerversand China nach Syrien',
    },
  },
  'fcl-shipping': {
    src: '/topics/fcl-shipping.jpg',
    alt: {
      ar: 'الشحن الكلي FCL حاوية كاملة',
      en: 'FCL full container load shipping',
      de: 'FCL Vollcontainerladung',
    },
  },
  'lcl-shipping': {
    src: '/topics/lcl-shipping.jpg',
    alt: {
      ar: 'الشحن الجزئي LCL تجميع بضائع في مستودع',
      en: 'LCL less-than-container-load warehouse consolidation',
      de: 'LCL Teilladung im Lager',
    },
  },
  'air-freight': {
    src: '/topics/air-freight.jpg',
    alt: {
      ar: 'الشحن الجوي للبضائع التجارية',
      en: 'Commercial air freight cargo aircraft',
      de: 'Luftfracht Verkehrsflugzeug',
    },
  },
};

export const topicPages: Record<TopicSlug, Record<SupportedLanguage, TopicCopy>> = {
  'customs-brokers-syria': {
    ar: {
      name: 'شركات التخليص في سوريا',
      title: 'شركات التخليص الجمركي في سوريا | الشراع — اللاذقية',
      description:
        'تبحث عن شركات التخليص الجمركي في سوريا؟ شركة الشراع في اللاذقية، سجل تجاري 10272، تخليص بحري وبري، حاويات ومبرّدات. الاستشارات ودراسة الأسعار خدمة مدفوعة. واتساب +963933612861.',
      h1: 'شركات التخليص الجمركي في سوريا',
      intro:
        'عند البحث عن شركات التخليص الجمركي في سوريا يظهر غالباً من لديه صفحة على خرائط Google، لا بالضرورة من ينجز التخليص فعلياً في المرفأ. شركة الشراع للتخليص الجمركي مقرها اللاذقية وتعمل عبر الموانئ والمعابر السورية.',
      paragraphs: [
        'الاسم التجاري المسجّل: شركة محمد حاج ياسين وخليل شلفون وشركاهم. الشكل القانوني شركة توصية بسيطة، رقم السجل التجاري 10272، تاريخ التسجيل 2025/04/13 لدى مديرية التجارة الداخلية وحماية المستهلك في اللاذقية. المقر: اللاذقية — الكاملية، عقار رقم 19/255. النشاط المرخّص: أنشطة التخليص الجمركي.',
        'الخدمة تشمل البيان الجمركي، التصنيف، متابعة الكشف، والإفراج عن البضاعة. نعمل على الحاويات العادية والمبرّدة، الشحن البحري عبر مرفأ اللاذقية، والشحن البري عبر المعابر ومنها معبر نصيب، مع تنسيق الشحن من الصين وأمريكا عند الطلب.',
        'اختيار شركة التخليص يعتمد على المنفذ ونوع البضاعة واكتمال المستندات، لا على ترتيب يظهر في محرك بحث. الاستشارات الجمركية ودراسة الأسعار خدمة مدفوعة وليست مجانية، ولا نبدأ الإجراءات قبل تأكيد الملف.',
        'للتواصل: واتساب +963933612861، البريد info@alshiraa-customs.com، والموقع https://alshiraa-customs.com/.',
      ],
      benefitsTitle: 'ما الذي تراجعونه قبل اختيار شركة تخليص؟',
      benefits: [
        'سجل تجاري سوري ونشاط تخليص جمركي معلن',
        'خبرة في المنفذ الفعلي: مرفأ اللاذقية أو المعبر البري',
        'تقدير رسوم مكتوب قبل الشحن',
        'متابعة حتى الإفراج، لا مجرد استلام الأوراق',
      ],
      faqTitle: 'أسئلة عن شركات التخليص الجمركي في سوريا',
      faqs: [
        {
          q: 'لماذا لا تظهر كل شركات التخليص عند البحث عن أفضل الشركات في سوريا؟',
          a: 'نتائج الخرائط تعتمد على صفحة Google Business والتقييمات، بينما نتائج المواقع تعتمد على الصفحات المفهرسة. وجود شركة في القائمة لا يعني أنها الأقوى في المرفأ.',
        },
        {
          q: 'أين مقر شركة الشراع؟',
          a: 'في اللاذقية — الكاملية، عقار رقم 19/255. التخليص يتم داخل سوريا عبر مرفأ اللاذقية والمعابر، لتجار سوريا والدول العربية.',
        },
        {
          q: 'هل تقدّمون تخليص حاويات ومبرّدات؟',
          a: 'نعم. نتابع الحاويات العادية والمبرّدة. الاستشارات ودراسة الأسعار خدمة مدفوعة قبل الالتزام.',
        },
      ],
      cta: 'تواصل عبر واتساب',
      quoteCta: 'اطلب عرض سعر',
    },
    en: {
      name: 'Customs brokers in Syria',
      title: 'Customs Clearance Companies in Syria | Al-Shiraa, Latakia',
      description:
        'Looking for a customs clearance company in Syria? Al-Shiraa is based in Latakia, commercial register 10272, sea and land clearance, containers and reefers. Customs advice and price studies are paid services.',
      h1: 'Customs Clearance Companies in Syria',
      intro:
        'Search results for customs brokers in Syria often list whoever has a Google Business profile. Al-Shiraa Customs Clearance is based in Latakia and clears cargo through Syrian ports and land borders.',
      paragraphs: [
        'Registered name: Mohammad Haj Yassin, Khalil Shalfoun & Partners. Simple limited partnership, commercial register 10272, registered 13 April 2025 with the Latakia directorate of internal trade. Office: Al-Kamaliya, Latakia, property 19/255. Licensed activity: customs clearance.',
        'Work covers the declaration, HS classification, inspection follow-up and release. We handle dry containers and reefers through Latakia Port, land borders including Nasib, and can coordinate China and USA shipping when requested.',
        'The right broker depends on the gateway, the cargo and complete documents — not on a search ranking. Customs consultations and price studies are paid services, and we do not start clearance before the file is confirmed.',
        'Contact: WhatsApp +963933612861, info@alshiraa-customs.com, https://alshiraa-customs.com/.',
      ],
      benefitsTitle: 'What to check before you choose a broker',
      benefits: [
        'A Syrian commercial register for customs clearance',
        'Experience at the real gateway: Latakia Port or the land border',
        'A written duty estimate before shipping',
        'Follow-up until release',
      ],
      faqTitle: 'FAQ — customs brokers in Syria',
      faqs: [
        {
          q: 'Why do some brokers not appear in “best customs companies in Syria”?',
          a: 'Map results follow Google Business profiles and reviews. Website results follow indexed pages. A map listing is not proof of port experience.',
        },
        {
          q: 'Where is Al-Shiraa based?',
          a: 'Al-Kamaliya, Latakia, property 19/255. Clearance is done inside Syria via Latakia Port and land borders.',
        },
        {
          q: 'Do you clear containers and reefers?',
          a: 'Yes. Dry containers and reefers. Customs consultations and price studies are paid services.',
        },
      ],
      cta: 'Chat on WhatsApp',
      quoteCta: 'Request a quote',
    },
    de: {
      name: 'Zollagenten in Syrien',
      title: 'Zollabfertigung in Syrien | Al-Shiraa, Latakia',
      description:
        'Zollabfertigung in Syrien: Al-Shiraa in Latakia, Handelsregister 10272, See und Land, Container und Kühlcontainer. Beratung und Preisstudien sind kostenpflichtig.',
      h1: 'Zollabfertigung in Syrien',
      intro:
        'Suchergebnisse zeigen oft Firmen mit Google-Unternehmensprofil. Al-Shiraa sitzt in Latakia und fertigt Waren über syrische Häfen und Landgrenzen ab.',
      paragraphs: [
        'Eingetragener Name: Mohammad Haj Yassin, Khalil Shalfoun & Partner. Handelsregister 10272, Sitz Al-Kamaliya, Latakia. Tätigkeit: Zollabfertigung.',
        'Leistung: Anmeldung, Tarifierung, Beschau und Freigabe. Trocken- und Kühlcontainer über den Hafen Latakia und Landgrenzen, auf Wunsch mit Versand aus China und den USA.',
        'Die passende Firma hängt vom Übergang und den Dokumenten ab, nicht von einem Suchrang. Zollberatung und Preisstudien sind kostenpflichtig.',
        'Kontakt: WhatsApp +963933612861, info@alshiraa-customs.com.',
      ],
      benefitsTitle: 'Worauf Sie achten sollten',
      benefits: [
        'Syrisches Handelsregister für Zoll',
        'Erfahrung am echten Übergang',
        'Schriftliche Schätzung vor dem Versand',
        'Begleitung bis zur Freigabe',
      ],
      faqTitle: 'FAQ — Zoll in Syrien',
      faqs: [
        {
          q: 'Warum fehlen manche Firmen in den Suchergebnissen?',
          a: 'Karten folgen Google-Unternehmensprofilen. Webseiten folgen indexierten Seiten. Ein Karteneintrag beweist keine Hafenerfahrung.',
        },
        {
          q: 'Wo sitzt Al-Shiraa?',
          a: 'Al-Kamaliya, Latakia. Die Abfertigung erfolgt in Syrien über den Hafen Latakia und die Landgrenzen.',
        },
      ],
      cta: 'Per WhatsApp schreiben',
      quoteCta: 'Angebot anfordern',
    },
  },

  'customs-clearance-latakia': {
    ar: {
      name: 'تخليص جمركي اللاذقية',
      title: 'تخليص جمركي في اللاذقية | الشراع للتخليص الجمركي',
      description:
        'تخليص جمركي في اللاذقية عبر مرفأ اللاذقية والمعابر السورية. الشراع: بيان جمركي ومتابعة الإفراج للبضائع التجارية. الاستشارات ودراسة الأسعار خدمة مدفوعة.',
      h1: 'تخليص جمركي في اللاذقية — سوريا',
      intro:
        'شركة الشراع للتخليص الجمركي مقرها اللاذقية، وتنجز إجراءات التخليص للشحنات التجارية عبر الموانئ والمعابر السورية بدقة وسرعة.',
      paragraphs: [
        'الباحث عن تخليص جمركي في اللاذقية يحتاج شريكاً يعرف مرفأ اللاذقية ومتطلبات البيان والتصنيف قبل وصول البضاعة. نراجع الملف مبكراً لتقليل التأخير وأرضيات التخزين.',
        'نخدم المستوردين والمصدّرين داخل سوريا وعملاء الدول العربية (الأردن، العراق، الإمارات، السعودية، لبنان وغيرها) عندما تكون الوجهة النهائية التخليص داخل سوريا.',
        'تشمل الخدمة: إعداد البيان الجمركي، متابعة الكشف، التنسيق مع الجهات المختصة، والإفراج عن البضاعة مع إبلاغكم في كل مرحلة.',
        'للبدء: أرسلوا نوع البضاعة، المنفذ المتوقع، والمستندات المتوفرة لنقدّم تقديراً أولياً للرسوم وأتعاب التخليص قبل أي التزام.',
      ],
      benefitsTitle: 'لماذا الشراع في اللاذقية؟',
      benefits: [
        'خبرة طويلة في التخليص الجمركي داخل سوريا',
        'تغطية مرفأ اللاذقية والمعابر البرية',
        'دراسة أسعار واستشارات جمركية مدفوعة قبل الالتزام',
        'متابعة حتى الإفراج والتسليم',
      ],
      faqTitle: 'أسئلة شائعة عن التخليص في اللاذقية',
      faqs: [
        {
          q: 'هل تقدّمون تخليصاً جمركياً في مرفأ اللاذقية؟',
          a: 'نعم. نتابع شحنات الحاويات والبضائع التجارية عبر مرفأ اللاذقية ونجهّز الملف قبل الوصول قدر الإمكان.',
        },
        {
          q: 'كم تستغرق عملية التخليص الجمركي؟',
          a: 'تختلف حسب نوع البضاعة واكتمال الوثائق؛ غالباً بضعة أيام عمل بعد اكتمال المستندات والكشف.',
        },
      ],
      cta: 'تواصل عبر واتساب',
      quoteCta: 'اطلب عرض سعر',
    },
    en: {
      name: 'Latakia customs clearance',
      title: 'Customs Clearance in Latakia | Al-Shiraa',
      description:
        'Customs clearance in Latakia via Latakia Port and Syrian gateways. Declaration filing and release follow-up for commercial cargo. Consultations and price studies are paid.',
      h1: 'Customs Clearance in Latakia, Syria',
      intro:
        'Al-Shiraa Customs Clearance is based in Latakia and handles commercial clearance through Syrian ports and land borders.',
      paragraphs: [
        'Importers searching for customs clearance in Latakia need a broker who knows Latakia Port procedures and HS classification before arrival.',
        'We serve Syrian traders and Arab-region shippers when clearance must be completed inside Syria.',
        'Services include declaration prep, inspection follow-up, authority coordination, and cargo release updates.',
        'Send cargo type, expected gateway, and available documents for an initial duty and fee estimate.',
      ],
      benefitsTitle: 'Why Al-Shiraa in Latakia?',
      benefits: [
        'Long experience in Syrian customs clearance',
        'Latakia Port and land-border coverage',
        'Paid customs advice and price study before commitment',
        'Follow-up until release',
      ],
      faqTitle: 'FAQ — Latakia clearance',
      faqs: [
        {
          q: 'Do you clear cargo at Latakia Port?',
          a: 'Yes. We handle commercial container and general cargo clearance through Latakia Port.',
        },
        {
          q: 'How long does clearance take?',
          a: 'It depends on cargo type and complete paperwork; often a few working days after documents and inspection.',
        },
      ],
      cta: 'Chat on WhatsApp',
      quoteCta: 'Request a quote',
    },
    de: {
      name: 'Zollabfertigung Latakia',
      title: 'Zollabfertigung in Latakia | Al-Shiraa',
      description:
        'Zollabfertigung in Latakia über den Hafen Latakia und syrische Übergänge. Kostenlose Abschätzung, Anmeldung und Freigabe.',
      h1: 'Zollabfertigung in Latakia, Syrien',
      intro:
        'Al-Shiraa sitzt in Latakia und erledigt Zollformalitäten für Handelswaren über Häfen und Landgrenzen.',
      paragraphs: [
        'Wer Zollabfertigung in Latakia sucht, braucht einen Partner mit Kenntnissen des Hafens und der Tarifierung vor Ankunft.',
        'Wir betreuen syrische und arabische Auftraggeber, wenn die Abfertigung in Syrien erfolgen muss.',
        'Leistung: Zollanmeldung, Beschau, Koordination und Freigabe mit laufender Information.',
        'Senden Sie Warentyp, geplanten Übergang und vorhandene Dokumente für eine erste Schätzung.',
      ],
      benefitsTitle: 'Warum Al-Shiraa in Latakia?',
      benefits: [
        'Lange Erfahrung in der syrischen Zollabfertigung',
        'Hafen Latakia und Landgrenzen',
        'Kostenlose Abschätzung vor Verpflichtung',
        'Begleitung bis zur Freigabe',
      ],
      faqTitle: 'FAQ — Latakia',
      faqs: [
        {
          q: 'Fertigen Sie im Hafen Latakia ab?',
          a: 'Ja. Wir betreuen kommerzielle Container- und Stückgutsendungen über den Hafen Latakia.',
        },
        {
          q: 'Wie lange dauert die Abfertigung?',
          a: 'Abhängig von Ware und vollständigen Papieren; oft wenige Werktage nach Dokumenten und Beschau.',
        },
      ],
      cta: 'Per WhatsApp schreiben',
      quoteCta: 'Angebot anfordern',
    },
  },

  'customs-duty-estimate': {
    ar: {
      name: 'تقدير رسوم جمركية',
      title: 'دراسة أسعار ورسوم جمركية | الشراع للتخليص الجمركي',
      description:
        'دراسة أسعار ورسوم جمركية قبل الاستيراد إلى سوريا حسب نوع البضاعة والبند الجمركي. الاستشارات ودراسة الأسعار خدمة مدفوعة.',
      h1: 'دراسة الرسوم الجمركية قبل الشحن',
      intro:
        'كثير من التجار يخسرون هامش الربح لأن الرسوم ظهرت أعلى من المتوقع. نقدّم دراسة أسعار واستشارات جمركية مدفوعة قبل الالتزام.',
      paragraphs: [
        'تعتمد الدراسة على وصف البضاعة، المنشأ، القيمة التقريبية، والبند الجمركي (HS). كلما كانت المعلومات أدق، كانت النتيجة أوضح.',
        'نستخدم خبرتنا في التصنيف الجمركي داخل سوريا مع مراجعة الوثائق المتوقعة لتقليل المفاجآت عند الوصول.',
        'النتيجة إرشادية وليست فاتورة نهائية؛ الرقم النهائي يعتمد على الكشف والتصنيف الرسمي لدى الجمارك. الاستشارات ودراسة الأسعار ليست مجانية.',
      ],
      benefitsTitle: 'ماذا تحتاجون للدراسة؟',
      benefits: ['وصف البضاعة', 'بلد المنشأ', 'قيمة تقريبية', 'المنفذ المتوقع (بحري/بري)'],
      faqTitle: 'أسئلة عن دراسة الرسوم',
      faqs: [
        {
          q: 'هل دراسة الرسوم مجانية؟',
          a: 'لا. الاستشارات الجمركية ودراسة الأسعار خدمة مدفوعة وليست مجانية.',
        },
      ],
      cta: 'اطلب دراسة عبر واتساب',
      quoteCta: 'نموذج عرض السعر',
    },
    en: {
      name: 'Duty estimate',
      title: 'Customs Duty & Price Study Syria | Al-Shiraa',
      description:
        'Paid customs advice and price study before importing into Syria, based on cargo type and HS classification.',
      h1: 'Customs Duty Study Before Shipping',
      intro:
        'Many traders lose margin when duties exceed expectations. We provide paid customs consultations and price studies before you commit.',
      paragraphs: [
        'Studies depend on cargo description, origin, approximate value, and HS code. Better inputs mean clearer guidance.',
        'We combine Syrian classification experience with document review to reduce surprises on arrival.',
        'Results are indicative, not a final invoice; official assessment follows inspection and customs classification. Consultations and price studies are not free.',
      ],
      benefitsTitle: 'What we need',
      benefits: ['Cargo description', 'Country of origin', 'Approx. value', 'Expected gateway'],
      faqTitle: 'Duty study FAQ',
      faqs: [
        {
          q: 'Is the duty study free?',
          a: 'No. Customs consultations and price studies are paid services.',
        },
      ],
      cta: 'Request a study on WhatsApp',
      quoteCta: 'Quote form',
    },
    de: {
      name: 'Zollschätzung',
      title: 'Zoll- und Preisstudie Syrien | Al-Shiraa',
      description:
        'Kostenpflichtige Zollberatung und Preisstudie vor dem Import nach Syrien anhand Warenart und HS-Code.',
      h1: 'Zollstudie vor dem Versand',
      intro:
        'Viele Händler verlieren Marge, wenn Abgaben höher ausfallen. Wir bieten kostenpflichtige Zollberatung und Preisstudien.',
      paragraphs: [
        'Die Studie hängt von Warenbeschreibung, Ursprung, Näherungswert und HS-Code ab.',
        'Wir kombinieren syrische Tarifierungserfahrung mit Dokumentenprüfung.',
        'Ergebnisse sind indikativ; die amtliche Bewertung folgt nach Beschau und Zollklassifikation. Beratung und Preisstudien sind nicht kostenlos.',
      ],
      benefitsTitle: 'Was wir brauchen',
      benefits: ['Warenbeschreibung', 'Ursprungsland', 'Näherungswert', 'Geplanter Übergang'],
      faqTitle: 'FAQ Zollstudie',
      faqs: [
        {
          q: 'Ist die Zollstudie kostenlos?',
          a: 'Nein. Zollberatung und Preisstudien sind kostenpflichtig.',
        },
      ],
      cta: 'Studie per WhatsApp anfragen',
      quoteCta: 'Angebotsformular',
    },
  },

  'container-customs-clearance': {
    ar: {
      name: 'تخليص حاويات',
      title: 'تخليص حاويات جمركياً في سوريا | الشراع',
      description:
        'تخليص حاويات 20 و40 قدم عبر مرفأ اللاذقية والمعابر. إعداد البيان، متابعة الكشف، والإفراج عن الحاوية التجارية.',
      h1: 'تخليص الحاويات الجمركي',
      intro: 'ننسّق تخليص الحاويات التجارية من الوصول حتى الإفراج، مع التركيز على اكتمال الوثائق قبل التفريغ.',
      paragraphs: [
        'تخليص الحاوية يتطلب تطابق الفاتورة وقائمة التعبئة وبوليصة الشحن مع الواقع. نراجع الملف مبكراً لتفادي وقف الحاوية.',
        'نخدم حاويات الاستيراد إلى سوريا عبر اللاذقية والمسارات المرتبطة بالمعابر البرية عند الحاجة.',
        'يمكن التنسيق مع شركة الشحن أو المورّد لإرسال المستندات قبل الإبحار أو فور صدورها.',
      ],
      benefitsTitle: 'يشمل التخليص',
      benefits: ['مراجعة مستندات الحاوية', 'بيان جمركي', 'متابعة الكشف', 'إفراج ومتابعة التسليم'],
      faqTitle: 'أسئلة عن الحاويات',
      faqs: [
        {
          q: 'هل تخليصون حاويات 40 قدم؟',
          a: 'نعم، نتابع تخليص الحاويات التجارية بما فيها 20 و40 قدم حسب نوع البضاعة والوثائق.',
        },
      ],
      cta: 'تواصل عبر واتساب',
      quoteCta: 'اطلب عرض سعر',
    },
    en: {
      name: 'Container clearance',
      title: 'Container Customs Clearance Syria | Al-Shiraa',
      description:
        'Customs clearance for 20\' and 40\' containers via Latakia Port and related gateways in Syria.',
      h1: 'Container Customs Clearance',
      intro:
        'We coordinate commercial container clearance from arrival to release, focusing on complete paperwork early.',
      paragraphs: [
        'Container clearance needs invoice, packing list and B/L to match the cargo. We review early to avoid holds.',
        'We clear import containers into Syria via Latakia and related land routes when needed.',
        'We can coordinate with the forwarder or supplier to receive documents before or right after sailing.',
      ],
      benefitsTitle: 'Included',
      benefits: ['Document review', 'Customs declaration', 'Inspection follow-up', 'Release coordination'],
      faqTitle: 'Container FAQ',
      faqs: [
        {
          q: 'Do you clear 40\' containers?',
          a: 'Yes. We handle commercial 20\' and 40\' containers subject to cargo type and documents.',
        },
      ],
      cta: 'Chat on WhatsApp',
      quoteCta: 'Request a quote',
    },
    de: {
      name: 'Container-Abfertigung',
      title: 'Container-Zollabfertigung Syrien | Al-Shiraa',
      description:
        'Zollabfertigung für 20- und 40-Fuß-Container über Latakia und verbundene Übergänge.',
      h1: 'Zollabfertigung von Containern',
      intro:
        'Wir koordinieren die Abfertigung kommerzieller Container von Ankunft bis Freigabe.',
      paragraphs: [
        'Rechnung, Packliste und Konnossement müssen zur Ware passen. Wir prüfen frühzeitig.',
        'Wir fertigen Importcontainer nach Syrien über Latakia und bei Bedarf Landrouten ab.',
        'Abstimmung mit Spediteur oder Lieferant für Dokumente vor oder direkt nach Abfahrt möglich.',
      ],
      benefitsTitle: 'Leistungsumfang',
      benefits: ['Dokumentenprüfung', 'Zollanmeldung', 'Beschau', 'Freigabe'],
      faqTitle: 'FAQ Container',
      faqs: [
        {
          q: 'Fertigen Sie 40-Fuß-Container ab?',
          a: 'Ja. Wir betreuen kommerzielle 20- und 40-Fuß-Container je nach Ware und Papieren.',
        },
      ],
      cta: 'Per WhatsApp schreiben',
      quoteCta: 'Angebot anfordern',
    },
  },

  'nasib-border-customs': {
    ar: {
      name: 'تخليص معبر نصيب',
      title: 'تخليص جمركي معبر نصيب | الشراع',
      description:
        'تخليص جمركي عبر معبر نصيب للشحنات البرية من الأردن والمنطقة. تجهيز الملف ومتابعة الإفراج داخل سوريا.',
      h1: 'تخليص جمركي عبر معبر نصيب',
      intro:
        'الشحنات البرية عبر نصيب تحتاج ملفاً مكتملاً قبل الوصول. نجهّز التخليص الجمركي ونتابع الإفراج داخل سوريا.',
      paragraphs: [
        'معبر نصيب مسار أساسي للبضائع القادمة من الأردن والأسواق المرتبطة. التأخير غالباً سببه نقص مستند أو تصنيف غير واضح.',
        'نراجع الفاتورة والمنشأ والتعبئة مسبقاً وننسّق مراحل الكشف والإفراج.',
        'يناسب تجار المواد الغذائية، مواد البناء، والآلات والشحنات التجارية العامة.',
      ],
      benefitsTitle: 'ماذا نقدّم عند نصيب؟',
      benefits: ['مراجعة الملف قبل الوصول', 'بيان ومتابعة برية', 'تنسيق الإفراج', 'تواصل مستمر مع العميل'],
      faqTitle: 'أسئلة عن نصيب',
      faqs: [
        {
          q: 'هل تعملون على شحنات نصيب من الأردن؟',
          a: 'نعم. نخدم شحنات التجار عبر معبر نصيب مع تجهيز التخليص داخل سوريا.',
        },
      ],
      cta: 'تواصل عبر واتساب',
      quoteCta: 'اطلب عرض سعر',
    },
    en: {
      name: 'Nasib border clearance',
      title: 'Nasib Border Customs Clearance | Al-Shiraa',
      description:
        'Customs clearance via Nasib border for land shipments from Jordan and the region into Syria.',
      h1: 'Customs Clearance via Nasib Border',
      intro:
        'Land shipments via Nasib need a complete file before arrival. We prepare clearance and follow release in Syria.',
      paragraphs: [
        'Nasib is a key corridor for cargo from Jordan and linked markets. Delays often come from missing papers or unclear classification.',
        'We review invoice, origin and packing early and coordinate inspection and release.',
        'Suitable for food, building materials, machinery and general commercial freight.',
      ],
      benefitsTitle: 'What we provide at Nasib',
      benefits: ['Pre-arrival file review', 'Land declaration follow-up', 'Release coordination', 'Client updates'],
      faqTitle: 'Nasib FAQ',
      faqs: [
        {
          q: 'Do you handle Nasib shipments from Jordan?',
          a: 'Yes. We clear commercial land shipments via Nasib into Syria.',
        },
      ],
      cta: 'Chat on WhatsApp',
      quoteCta: 'Request a quote',
    },
    de: {
      name: 'Zoll Nasib',
      title: 'Zollabfertigung Grenzübergang Nasib | Al-Shiraa',
      description:
        'Zollabfertigung über Nasib für Landtransporte aus Jordanien und der Region nach Syrien.',
      h1: 'Zollabfertigung über Nasib',
      intro:
        'Landtransporte über Nasib brauchen eine vollständige Akte vor Ankunft. Wir bereiten ab und begleiten die Freigabe.',
      paragraphs: [
        'Nasib ist ein Hauptkorridor aus Jordanien. Verzögerungen entstehen oft durch fehlende Papiere.',
        'Wir prüfen Rechnung, Ursprung und Packliste frühzeitig.',
        'Geeignet für Lebensmittel, Baumaterial, Maschinen und allgemeine Handelsware.',
      ],
      benefitsTitle: 'Leistung bei Nasib',
      benefits: ['Aktenprüfung vor Ankunft', 'Begleitung Landzoll', 'Freigabe', 'Kundenupdates'],
      faqTitle: 'FAQ Nasib',
      faqs: [
        {
          q: 'Betreuen Sie Nasib-Sendungen aus Jordanien?',
          a: 'Ja. Wir fertigen kommerzielle Landtransporte über Nasib nach Syrien ab.',
        },
      ],
      cta: 'Per WhatsApp schreiben',
      quoteCta: 'Angebot anfordern',
    },
  },

  'reefer-customs-clearance': {
    ar: {
      name: 'تخليص براد / reefer',
      title: 'تخليص حاويات مبرّدة (Reefer) | الشراع اللاذقية',
      description:
        'تخليص جمركي للحاويات المبرّدة والأغذية الحساسة للحرارة عبر مرفأ اللاذقية، مع تجهيز الوثائق ومتابعة الإفراج.',
      h1: 'تخليص الحاويات المبرّدة (Reefer)',
      intro:
        'البضائع المبرّدة لا تحتمل تأخير الوثائق. نجهّز ملف التخليص مبكراً ونتابع الإفراج في اللاذقية للشحنات التجارية المبرّدة.',
      paragraphs: [
        'الحاوية المبرّدة (reefer) للأغذية والشوكولا والمشروبات وغيرها تحتاج مستندات مكتملة وشروطاً صحية حسب الصنف.',
        'نساعد في ترتيب الملف قبل الوصول وتنسيق التخليص لتقليل زمن الانتظار على الرصيف أو في الساحة.',
        'أخبرونا بدرجة الحرارة ونوع البضاعة والمنفذ لنجهّز تقديراً وخطة تخليص.',
      ],
      benefitsTitle: 'تركيزنا في شحنات البراد',
      benefits: ['مراجعة وثائق الأغذية', 'تنسيق الوصول والتخليص', 'متابعة الإفراج', 'تواصل سريع عند أي نقص'],
      faqTitle: 'أسئلة عن البراد',
      faqs: [
        {
          q: 'هل تخليصون شحنات غذائية مبرّدة؟',
          a: 'نعم، نتابع التخليص الجمركي لشحنات تجارية مبرّدة عبر اللاذقية وفق الوثائق والمتطلبات المعمول بها.',
        },
      ],
      cta: 'تواصل عبر واتساب',
      quoteCta: 'اطلب عرض سعر',
    },
    en: {
      name: 'Reefer clearance',
      title: 'Reefer Container Customs Clearance Latakia | Al-Shiraa',
      description:
        'Customs clearance for refrigerated (reefer) containers and temperature-sensitive food via Latakia Port.',
      h1: 'Reefer Container Customs Clearance',
      intro:
        'Cold cargo cannot wait on paperwork. We prepare the clearance file early and follow release in Latakia.',
      paragraphs: [
        'Reefer shipments for food, chocolate, beverages and similar goods need complete documents and product-specific certificates.',
        'We help arrange the file before arrival to reduce dwell time at the terminal.',
        'Tell us set-point, product type and gateway for an estimate and clearance plan.',
      ],
      benefitsTitle: 'Our focus on reefer cargo',
      benefits: ['Food document review', 'Arrival/clearance coordination', 'Release follow-up', 'Fast gap alerts'],
      faqTitle: 'Reefer FAQ',
      faqs: [
        {
          q: 'Do you clear refrigerated food containers?',
          a: 'Yes. We handle commercial reefer clearance via Latakia subject to documents and applicable rules.',
        },
      ],
      cta: 'Chat on WhatsApp',
      quoteCta: 'Request a quote',
    },
    de: {
      name: 'Reefer-Abfertigung',
      title: 'Reefer-Zollabfertigung Latakia | Al-Shiraa',
      description:
        'Zollabfertigung für Kühlcontainer (Reefer) und temperaturempfindliche Lebensmittel über Latakia.',
      h1: 'Zollabfertigung von Reefer-Containern',
      intro:
        'Kühlgut verträgt keinen Papierkram-Stau. Wir bereiten die Akte früh vor und begleiten die Freigabe in Latakia.',
      paragraphs: [
        'Reefer-Sendungen brauchen vollständige Dokumente und produktspezifische Zertifikate.',
        'Wir helfen, die Akte vor Ankunft zu ordnen und Standzeiten zu reduzieren.',
        'Nennen Sie Solltemperatur, Produkt und Übergang für Schätzung und Plan.',
      ],
      benefitsTitle: 'Fokus Reefer',
      benefits: ['Lebensmitteldokumente', 'Ankunft/Abfertigung', 'Freigabe', 'Schnelle Hinweise bei Lücken'],
      faqTitle: 'FAQ Reefer',
      faqs: [
        {
          q: 'Fertigen Sie Kühllebensmittel ab?',
          a: 'Ja. Wir betreuen kommerzielle Reefer-Abfertigung über Latakia gemäß Dokumenten und Vorschriften.',
        },
      ],
      cta: 'Per WhatsApp schreiben',
      quoteCta: 'Angebot anfordern',
    },
  },

  'shipping-usa-syria': {
    ar: {
      name: 'شحن أمريكا ↔ سوريا',
      title: 'شحن من أمريكا إلى سوريا وبالعكس | الشراع',
      description:
        'تنسيق الشحن الكلي والجزئي والجوي من الولايات المتحدة إلى سوريا وبالعكس، مع التخليص الجمركي في اللاذقية. اطلب عرض سعر.',
      h1: 'شحن من أمريكا إلى سوريا وبالعكس',
      intro:
        'نساعد التجار على تنسيق شحنات أمريكا–سوريا (بحري وجوي) مع تجهيز ملف التخليص الجمركي في اللاذقية حتى الإفراج.',
      paragraphs: [
        'مسار أمريكا → سوريا مناسب للأغذية، الإلكترونيات، قطع الغيار، والآلات. ننسّق مع فورواردرات مرخّصة للشحن الكلي (FCL) أو الجزئي (LCL) أو الجوي، ونتولى التخليص عند الوصول.',
        'مسار سوريا → أمريكا يحتاج مستندات تصدير وتصنيفاً دقيقاً. نجهّز الجانب الجمركي داخل سوريا وننسّق المتطلبات مع وكيل الشحن.',
        'للعرض: أرسلوا نوع البضاعة، الوزن/الحجم التقريبي، الميناء أو المطار المتوقع، وهل تحتاجون حاوية كاملة أم شحناً جزئياً أم جواً.',
      ],
      benefitsTitle: 'ماذا نقدّم على مسار أمريكا؟',
      benefits: [
        'تنسيق FCL / LCL / جوي',
        'تخليص جمركي في اللاذقية',
        'مراجعة المستندات قبل الإبحار/الإقلاع',
        'تقدير أولي للتكاليف قبل الالتزام',
      ],
      faqTitle: 'أسئلة عن شحن أمريكا',
      faqs: [
        {
          q: 'هل تشحنون مباشرة من ميامي أو نيويورك؟',
          a: 'ننسّق الشحن عبر وكلاء وموانئ أمريكية مناسبة لشحنتكم (مثل ميامي أو موانئ الساحل الشرقي) مع التخليص في سوريا.',
        },
        {
          q: 'هل يشمل العرض التخليص في اللاذقية؟',
          a: 'نعم. التخليص الجمركي داخل سوريا هو اختصاصنا الأساسي، ونربطه بتنسيق الشحن عند الطلب.',
        },
      ],
      cta: 'اطلب عرض شحن عبر واتساب',
      quoteCta: 'نموذج عرض السعر',
    },
    en: {
      name: 'USA ↔ Syria shipping',
      title: 'Shipping USA to Syria & Back | Al-Shiraa',
      description:
        'Coordinate FCL, LCL and air freight between the USA and Syria, plus Latakia customs clearance. Request a quote.',
      h1: 'Shipping between the USA and Syria',
      intro:
        'We help traders coordinate USA–Syria ocean and air shipments and prepare Syrian customs clearance in Latakia through release.',
      paragraphs: [
        'USA → Syria suits food, electronics, spare parts and machinery. We coordinate licensed forwarders for FCL, LCL or air, then clear on arrival.',
        'Syria → USA needs export papers and accurate classification. We handle the Syrian customs side and align requirements with the forwarder.',
        'For a quote: send cargo type, approx. weight/volume, preferred port/airport, and whether you need FCL, LCL or air.',
      ],
      benefitsTitle: 'What we offer on the USA lane',
      benefits: [
        'FCL / LCL / air coordination',
        'Latakia customs clearance',
        'Document review before sailing/departure',
        'Preliminary cost estimate',
      ],
      faqTitle: 'USA shipping FAQ',
      faqs: [
        {
          q: 'Do you ship from Miami or New York?',
          a: 'We coordinate via suitable US ports (e.g. Miami or East Coast) with clearance in Syria.',
        },
        {
          q: 'Does the quote include Latakia clearance?',
          a: 'Yes. Syrian clearance is our core service and can be bundled with freight coordination.',
        },
      ],
      cta: 'Request shipping quote on WhatsApp',
      quoteCta: 'Quote form',
    },
    de: {
      name: 'USA ↔ Syrien Versand',
      title: 'Versand USA–Syrien und zurück | Al-Shiraa',
      description:
        'Koordination von FCL, LCL und Luftfracht zwischen USA und Syrien plus Zoll in Latakia.',
      h1: 'Versand zwischen USA und Syrien',
      intro:
        'Wir koordinieren USA–Syrien See- und Luftsendungen und bereiten die Zollabfertigung in Latakia vor.',
      paragraphs: [
        'USA → Syrien eignet sich für Lebensmittel, Elektronik, Ersatzteile und Maschinen — FCL, LCL oder Luft.',
        'Syrien → USA braucht Exportpapiere und klare Tarifierung. Wir übernehmen die syrische Zollseite.',
        'Für ein Angebot: Warentyp, Gewicht/Volumen, Hafen/Flughafen und FCL/LCL/Luft angeben.',
      ],
      benefitsTitle: 'Leistung USA-Route',
      benefits: ['FCL / LCL / Luft', 'Zoll Latakia', 'Dokumentenprüfung', 'Kostenschätzung'],
      faqTitle: 'FAQ USA',
      faqs: [
        {
          q: 'Versenden Sie ab Miami oder New York?',
          a: 'Wir koordinieren geeignete US-Häfen mit Abfertigung in Syrien.',
        },
      ],
      cta: 'Angebot per WhatsApp',
      quoteCta: 'Angebotsformular',
    },
  },

  'shipping-china-syria': {
    ar: {
      name: 'شحن الصين ↔ سوريا',
      title: 'شحن من الصين إلى سوريا وبالعكس | الشراع',
      description:
        'شحن حاويات كلي وجزئي وجوي من الصين إلى سوريا وبالعكس، مع التخليص الجمركي في مرفأ اللاذقية. عرض سعر سريع.',
      h1: 'شحن من الصين إلى سوريا وبالعكس',
      intro:
        'مسار الصين–سوريا من أكثر المسارات طلباً للتجار. ننسّق الشحن البحري/الجوي ونتولى التخليص عند الوصول إلى اللاذقية.',
      paragraphs: [
        'الشحن الكلي (FCL) مناسب للكميات الكبيرة من الصين (قوانغتشو، شنغهاي، ييوو وغيرها). الشحن الجزئي (LCL) يناسب الكميات الأصغر بتكلفة أقل.',
        'الشحن الجوي من الصين يُستخدم للطوارئ والبضائع عالية القيمة. نوضح الفرق في الزمن والتكلفة قبل الحجز.',
        'عند التصدير من سوريا إلى الصين نجهّز الجانب الجمركي المحلي وننسّق المستندات مع وكيل الشحن.',
      ],
      benefitsTitle: 'خدمات مسار الصين',
      benefits: ['FCL و LCL من الصين', 'خيار الشحن الجوي', 'تخليص اللاذقية', 'متابعة المستندات والإفراج'],
      faqTitle: 'أسئلة عن شحن الصين',
      faqs: [
        {
          q: 'هل تدعمون الشحن من ييوو أو شنغهاي؟',
          a: 'نعم، ننسّق مع مسارات الصين الشائعة ونحدد الميناء الأنسب حسب البضاعة والوقت.',
        },
        {
          q: 'ما الفرق بين الشحن الكلي والجزئي من الصين؟',
          a: 'الكلي (FCL) حاوية كاملة لكم؛ الجزئي (LCL) تشاركون الحاوية مع شحنات أخرى — أوفر للكميات الصغيرة.',
        },
      ],
      cta: 'اطلب عرض شحن الصين',
      quoteCta: 'نموذج عرض السعر',
    },
    en: {
      name: 'China ↔ Syria shipping',
      title: 'Shipping China to Syria & Back | Al-Shiraa',
      description:
        'FCL, LCL and air freight from China to Syria and back, with Latakia Port customs clearance. Fast quote.',
      h1: 'Shipping between China and Syria',
      intro:
        'China–Syria is a high-demand trade lane. We coordinate ocean/air freight and clear cargo on arrival in Latakia.',
      paragraphs: [
        'FCL fits large volumes from China (Guangzhou, Shanghai, Yiwu and others). LCL fits smaller lots at lower cost.',
        'Air freight from China is for urgent or high-value cargo. We explain time vs cost before booking.',
        'For Syria → China we prepare local customs and align documents with the forwarder.',
      ],
      benefitsTitle: 'China lane services',
      benefits: ['FCL & LCL from China', 'Air option', 'Latakia clearance', 'Docs and release follow-up'],
      faqTitle: 'China shipping FAQ',
      faqs: [
        {
          q: 'Do you support Yiwu or Shanghai origins?',
          a: 'Yes. We coordinate common China origins and pick the best port for cargo and timing.',
        },
        {
          q: 'FCL vs LCL from China?',
          a: 'FCL is your full container; LCL shares space — usually cheaper for small volumes.',
        },
      ],
      cta: 'Request China shipping quote',
      quoteCta: 'Quote form',
    },
    de: {
      name: 'China ↔ Syrien Versand',
      title: 'Versand China–Syrien und zurück | Al-Shiraa',
      description:
        'FCL, LCL und Luftfracht China–Syrien mit Zollabfertigung Latakia.',
      h1: 'Versand zwischen China und Syrien',
      intro:
        'China–Syrien ist eine stark nachgefragte Route. Wir koordinieren See-/Luftfracht und fertigen in Latakia ab.',
      paragraphs: [
        'FCL für große Mengen; LCL für kleinere Lose. Luftfracht für dringende oder hochwertige Ware.',
        'Syrien → China: lokale Zollseite und Abstimmung mit dem Spediteur.',
      ],
      benefitsTitle: 'Leistung China',
      benefits: ['FCL & LCL', 'Luftoption', 'Zoll Latakia', 'Dokumente & Freigabe'],
      faqTitle: 'FAQ China',
      faqs: [
        {
          q: 'Yiwu oder Shanghai?',
          a: 'Ja, wir koordinieren gängige China-Herkünfte und passende Häfen.',
        },
      ],
      cta: 'China-Angebot per WhatsApp',
      quoteCta: 'Angebotsformular',
    },
  },

  'china-fcl-lcl': {
    ar: {
      name: 'شحن كلي وجزئي من الصين',
      title: 'شحن كلي وجزئي من الصين | أسرع وأرخص سعر | احجز مكانك الآن',
      description:
        'شحن بحري كلي FCL وجزئي LCL من الصين إلى مرفأ اللاذقية. نقارن المسار الأسرع والسعر الأنسب ثم تحجزون مكانكم الآن عبر واتساب.',
      h1: 'شحن كلي وجزئي من الصين — احجز مكانك الآن',
      intro:
        'حاوية كاملة أو جزء من حاوية من الصين إلى سوريا. نبحث عن أقرب إبحار عملي وأقل سعر مناسب لحجم شحنتكم، ثم نثبت المكان قبل إغلاق التجميع.',
      paragraphs: [
        'الشحن الكلي (FCL) حاوية 20 أو 40 قدماً لشحنتكم وحدها. يناسب الكميات الكبيرة وغالباً أوفر للطن عندما تقترب البضاعة من ملء الحاوية.',
        'الشحن الجزئي (LCL) للكميات التي لا تملأ حاوية. تُحسب التكلفة حسب الحجم (CBM) والوزن، وهذا المسار الأرخص عادةً للعينات والكميات المتوسطة من ييوو وقوانغتشو وشنغهاي.',
        'الأسرع ليس دائماً الأرخص. نعرض الفرق بين أقرب رحلة بحرية والخيار الأقل تكلفة، ثم تحجزون المكان على المسار الذي تختارونه.',
        'بعد الحجز نجهّز التخليص في مرفأ اللاذقية. أرسلوا نوع البضاعة، المدينة في الصين، والوزن أو الحجم التقريبي.',
      ],
      benefitsTitle: 'ماذا يشمل الحجز؟',
      benefits: [
        'شحن كلي FCL من الصين',
        'شحن جزئي LCL بسعر حسب الحجم',
        'مقارنة الأسرع والأرخص قبل التثبيت',
        'تخليص جمركي في اللاذقية عند الوصول',
      ],
      faqTitle: 'أسئلة الحجز من الصين',
      faqs: [
        {
          q: 'كيف أحجز مكاني؟',
          a: 'اضغطوا «احجز مكانك الآن» وأرسلوا نوع البضاعة والمدينة في الصين والوزن أو CBM. نرد بخيار الأسرع وخيار الأرخص ثم نثبت الحجز بعد موافقتكم.',
        },
        {
          q: 'أيهما أرخص: الكلي أم الجزئي؟',
          a: 'الجزئي أرخص للكميات الصغيرة. الكلي يصبح أوفر عندما تقترب الشحنة من ملء حاوية 20 أو 40 قدماً.',
        },
        {
          q: 'هل السعر ثابت على الصفحة؟',
          a: 'سعر البحر يتغير أسبوعياً حسب الميناء والموسم. نؤكد السعر الحالي على واتساب قبل الحجز، بدون رقم تقديري قديم على الصفحة.',
        },
      ],
      cta: 'احجز مكانك الآن',
      quoteCta: 'نموذج عرض السعر',
    },
    en: {
      name: 'China FCL & LCL',
      title: 'FCL & LCL from China | Fastest & Lowest Rate | Book Now',
      description:
        'FCL and LCL ocean freight from China to Latakia. We compare the fastest sailing and the best rate, then you book your space.',
      h1: 'FCL and LCL from China — book your space',
      intro:
        'A full container or a share of one, from China to Syria. We look for the next practical sailing and the lowest suitable rate, then hold the space.',
      paragraphs: [
        'FCL is your own 20\' or 40\' container — usually better value per ton when the cargo nearly fills the box.',
        'LCL is priced by CBM and weight. It is usually the cheaper path for samples and mid-size lots from Yiwu, Guangzhou and Shanghai.',
        'Fastest and cheapest are not always the same sailing. We show both, and you book the one you want.',
      ],
      benefitsTitle: 'What booking includes',
      benefits: ['FCL from China', 'LCL by volume', 'Fastest vs lowest rate before you confirm', 'Latakia customs on arrival'],
      faqTitle: 'Booking FAQ',
      faqs: [
        {
          q: 'How do I book?',
          a: 'Tap Book now and send cargo type, China city, and weight or CBM. We reply with the faster option and the cheaper option.',
        },
        {
          q: 'Are prices listed on the page?',
          a: 'Ocean rates change weekly. We confirm the current rate on WhatsApp before booking.',
        },
      ],
      cta: 'Book your space now',
      quoteCta: 'Quote form',
    },
    de: {
      name: 'China FCL & LCL',
      title: 'FCL & LCL ab China | Schnellster und günstigster Weg | Platz sichern',
      description:
        'FCL und LCL von China nach Latakia. Wir vergleichen die schnellste Abfahrt und den passenden Preis, dann sichern Sie den Platz.',
      h1: 'FCL und LCL ab China — Platz sichern',
      intro:
        'Vollcontainer oder Teilladung von China nach Syrien. Wir suchen die nächste praktikable Abfahrt und den günstigsten passenden Preis.',
      paragraphs: [
        'FCL: eigener 20- oder 40-Fuß-Container. LCL: Abrechnung nach CBM und Gewicht, meist günstiger für kleine Mengen.',
        'Schnellste und günstigste Abfahrt sind nicht immer dieselbe. Sie wählen, dann halten wir den Platz.',
      ],
      benefitsTitle: 'Leistung',
      benefits: ['FCL ab China', 'LCL nach Volumen', 'Vergleich vor der Buchung', 'Zoll Latakia'],
      faqTitle: 'FAQ',
      faqs: [
        {
          q: 'Wie buche ich?',
          a: 'WhatsApp mit Warenart, Stadt in China und Gewicht oder CBM. Wir nennen die schnellere und die günstigere Option.',
        },
      ],
      cta: 'Platz jetzt sichern',
      quoteCta: 'Angebotsformular',
    },
  },

  'fcl-shipping': {
    ar: {
      name: 'الشحن الكلي FCL',
      title: 'الشحن الكلي FCL حاوية كاملة | الشراع',
      description:
        'الشحن الكلي (Full Container Load): حاوية 20 أو 40 قدم لكم وحدكم من أمريكا أو الصين إلى سوريا، مع التخليص في اللاذقية.',
      h1: 'الشحن الكلي (FCL) — حاوية كاملة',
      intro:
        'الشحن الكلي يعني أن الحاوية مخصّصة لشحنتكم فقط. مناسب للكميات الكبيرة وأفضل غالباً من حيث الأمان وسهولة التتبع.',
      paragraphs: [
        'نستخدم FCL لشحنات أمريكا والصين نحو مرفأ اللاذقية عندما يمتلئ الحجم أو الوزن بما يبرّر حاوية كاملة.',
        'نساعد في اختيار 20′ أو 40′ أو 40′HC، وننسّق الحجز مع التخليص الجمركي عند الوصول.',
        'اطلبوا عرضاً يذكر: المنشأ، الوجهة، نوع البضاعة، والأبعاد/الوزن التقريبي.',
      ],
      benefitsTitle: 'مزايا FCL',
      benefits: ['حاوية كاملة لكم', 'مناسب للكميات الكبيرة', 'تتبع أوضح', 'ربط مع تخليص اللاذقية'],
      faqTitle: 'أسئلة FCL',
      faqs: [
        {
          q: 'متى أختار الشحن الكلي بدل الجزئي؟',
          a: 'عندما يكون حجمكم قريباً من ملء الحاوية، أو تحتاجون سرعة/خصوصية أعلى من التجميع المشترك.',
        },
      ],
      cta: 'اطلب عرض FCL',
      quoteCta: 'نموذج عرض السعر',
    },
    en: {
      name: 'FCL shipping',
      title: 'FCL Full Container Load Shipping | Al-Shiraa',
      description:
        'Full Container Load (FCL): your own 20\' or 40\' container from USA or China to Syria, with Latakia clearance.',
      h1: 'FCL — Full Container Load',
      intro:
        'FCL means the container is dedicated to your cargo. Best for larger volumes, security and clearer tracking.',
      paragraphs: [
        'We use FCL on USA and China lanes to Latakia when volume/weight justifies a full box.',
        'We help choose 20\', 40\' or 40\'HC and link booking with arrival clearance.',
      ],
      benefitsTitle: 'FCL benefits',
      benefits: ['Dedicated container', 'Better for large lots', 'Clearer tracking', 'Latakia clearance link'],
      faqTitle: 'FCL FAQ',
      faqs: [
        {
          q: 'When to choose FCL over LCL?',
          a: 'When you nearly fill a container, or need more privacy/speed than shared consolidation.',
        },
      ],
      cta: 'Request FCL quote',
      quoteCta: 'Quote form',
    },
    de: {
      name: 'FCL Versand',
      title: 'FCL Vollcontainer | Al-Shiraa',
      description:
        'FCL: eigener 20-/40-Fuß-Container USA/China nach Syrien mit Zoll Latakia.',
      h1: 'FCL — Vollcontainerladung',
      intro: 'FCL heißt: der Container ist nur für Ihre Ware — ideal für größere Mengen.',
      paragraphs: [
        'Wir nutzen FCL auf USA- und China-Routen nach Latakia, wenn Volumen/Gewicht es rechtfertigt.',
      ],
      benefitsTitle: 'Vorteile FCL',
      benefits: ['Eigener Container', 'Große Mengen', 'Tracking', 'Zoll Latakia'],
      faqTitle: 'FAQ FCL',
      faqs: [
        {
          q: 'FCL oder LCL?',
          a: 'FCL wenn Sie den Container fast füllen oder mehr Privatsphäre brauchen.',
        },
      ],
      cta: 'FCL-Angebot',
      quoteCta: 'Angebotsformular',
    },
  },

  'lcl-shipping': {
    ar: {
      name: 'الشحن الجزئي LCL',
      title: 'الشحن الجزئي LCL | الشراع للتخليص والشحن',
      description:
        'الشحن الجزئي (Less than Container Load): شحن كميات صغيرة من أمريكا أو الصين ضمن حاوية مشتركة إلى سوريا مع التخليص.',
      h1: 'الشحن الجزئي (LCL)',
      intro:
        'الشحن الجزئي يناسب التجار الذين لا يملؤون حاوية كاملة. تُجمَّع شحنتكم مع بضائع أخرى في حاوية واحدة بتكلفة أقل.',
      paragraphs: [
        'LCL شائع للاستيراد من الصين وأمريكا عندما تكون الكمية متوسطة أو تجريبية.',
        'نحتاج وصفاً دقيقاً ووزناً وحجماً (CBM) لتقدير النولون، ثم نربط الشحنة بالتخليص في اللاذقية.',
        'مدة LCL قد تكون أطول قليلاً من FCL بسبب التجميع في المنشأ — نوضح الجدول عند العرض.',
      ],
      benefitsTitle: 'مزايا LCL',
      benefits: ['تكلفة أقل للكميات الصغيرة', 'مرونة للكميات التجريبية', 'من الصين وأمريكا', 'تخليص عند الوصول'],
      faqTitle: 'أسئلة LCL',
      faqs: [
        {
          q: 'هل الشحن الجزئي آمن مثل الكلي؟',
          a: 'نعم عند اختيار وكيل موثوق وتعبئة صحيحة؛ نفرّق العرض حسب المنشأ ونوع البضاعة.',
        },
      ],
      cta: 'اطلب عرض LCL',
      quoteCta: 'نموذج عرض السعر',
    },
    en: {
      name: 'LCL shipping',
      title: 'LCL Less-than-Container Shipping | Al-Shiraa',
      description:
        'LCL: ship smaller lots from USA or China in a shared container to Syria, plus customs clearance.',
      h1: 'LCL — Less than Container Load',
      intro:
        'LCL suits traders who do not fill a full container. Your cargo consolidates with others at lower cost.',
      paragraphs: [
        'Common for China and USA imports when volume is medium or trial-sized.',
        'We need accurate description, weight and CBM for freight estimate, then Latakia clearance.',
      ],
      benefitsTitle: 'LCL benefits',
      benefits: ['Lower cost for small lots', 'Flexible for trials', 'China & USA', 'Clearance on arrival'],
      faqTitle: 'LCL FAQ',
      faqs: [
        {
          q: 'Is LCL as safe as FCL?',
          a: 'With a reliable consolidator and proper packing, yes — we quote by origin and cargo type.',
        },
      ],
      cta: 'Request LCL quote',
      quoteCta: 'Quote form',
    },
    de: {
      name: 'LCL Versand',
      title: 'LCL Teilladung | Al-Shiraa',
      description:
        'LCL: kleinere Mengen USA/China in Sammelcontainer nach Syrien plus Zoll.',
      h1: 'LCL — Teilladung',
      intro: 'LCL passt, wenn Sie keinen Vollcontainer füllen — günstiger für kleine Lose.',
      paragraphs: [
        'Häufig bei China- und USA-Importen mit mittlerem oder Testvolumen.',
      ],
      benefitsTitle: 'Vorteile LCL',
      benefits: ['Günstiger bei kleinen Mengen', 'Flexibel', 'China & USA', 'Zoll bei Ankunft'],
      faqTitle: 'FAQ LCL',
      faqs: [
        {
          q: 'LCL sicher?',
          a: 'Mit zuverlässigem Konsolidierer und guter Verpackung — ja.',
        },
      ],
      cta: 'LCL-Angebot',
      quoteCta: 'Angebotsformular',
    },
  },

  'air-freight': {
    ar: {
      name: 'الشحن الجوي',
      title: 'الشحن الجوي من أمريكا والصين إلى سوريا | الشراع',
      description:
        'الشحن الجوي للبضائع العاجلة وعالية القيمة من أمريكا والصين إلى سوريا، مع التخليص الجمركي ومتابعة الإفراج.',
      h1: 'الشحن الجوي للبضائع التجارية',
      intro:
        'الشحن الجوي أسرع من البحري وأعلى تكلفة. ننسّقه للشحنات العاجلة أو الحساسة للوقت، مع تجهيز التخليص فور الوصول.',
      paragraphs: [
        'من أمريكا والصين يمكن شحن العينات، الإلكترونيات، قطع الغيار، والأدوية/المستلزمات حسب الامتثال والوثائق.',
        'نحتاج الوزن الحجمي والفعلي ووصف البضاعة لتقدير النولون الجوي، ثم مسار التخليص في سوريا.',
        'للشحنات الكبيرة يبقى البحري (FCL/LCL) أوفر عادةً — نساعدكم على اختيار الأنسب.',
      ],
      benefitsTitle: 'متى تختارون الجوي؟',
      benefits: ['سرعة أعلى', 'مناسب للعيّنات والطوارئ', 'من أمريكا والصين', 'تخليص سريع عند الوصول'],
      faqTitle: 'أسئلة الشحن الجوي',
      faqs: [
        {
          q: 'هل الشحن الجوي يشمل التخليص؟',
          a: 'يمكن دمج تنسيق الشحن الجوي مع خدمة التخليص الجمركي لدى الشراع حسب الاتفاق.',
        },
      ],
      cta: 'اطلب عرض شحن جوي',
      quoteCta: 'نموذج عرض السعر',
    },
    en: {
      name: 'Air freight',
      title: 'Air Freight USA & China to Syria | Al-Shiraa',
      description:
        'Air freight for urgent and high-value cargo from the USA and China to Syria, with customs clearance follow-up.',
      h1: 'Commercial Air Freight',
      intro:
        'Air is faster and costlier than ocean. We coordinate it for urgent shipments and prepare clearance on arrival.',
      paragraphs: [
        'From USA and China: samples, electronics, spare parts and compliant regulated goods when documents allow.',
        'We need chargeable weight and cargo details for the air quote, then Syrian clearance.',
        'For large volumes ocean FCL/LCL is usually cheaper — we help you choose.',
      ],
      benefitsTitle: 'When to choose air',
      benefits: ['Higher speed', 'Samples & emergencies', 'USA & China', 'Fast clearance on arrival'],
      faqTitle: 'Air freight FAQ',
      faqs: [
        {
          q: 'Does air freight include clearance?',
          a: 'We can bundle air coordination with Al-Shiraa customs clearance as agreed.',
        },
      ],
      cta: 'Request air freight quote',
      quoteCta: 'Quote form',
    },
    de: {
      name: 'Luftfracht',
      title: 'Luftfracht USA & China nach Syrien | Al-Shiraa',
      description:
        'Luftfracht für dringende und hochwertige Ware USA/China nach Syrien mit Zollbegleitung.',
      h1: 'Kommerzielle Luftfracht',
      intro:
        'Luft ist schneller und teurer als See. Wir koordinieren dringende Sendungen und die Abfertigung bei Ankunft.',
      paragraphs: [
        'Geeignet für Muster, Elektronik und Ersatzteile — je nach Dokumentenlage.',
        'Für große Mengen ist See-FCL/LCL meist günstiger.',
      ],
      benefitsTitle: 'Wann Luft?',
      benefits: ['Schneller', 'Muster & Notfälle', 'USA & China', 'Schnelle Abfertigung'],
      faqTitle: 'FAQ Luftfracht',
      faqs: [
        {
          q: 'Inklusive Zoll?',
          a: 'Luftkoordination kann mit Zoll von Al-Shiraa kombiniert werden.',
        },
      ],
      cta: 'Luftfracht-Angebot',
      quoteCta: 'Angebotsformular',
    },
  },
};
